/**
 * Everything the Reporting page can DO: run a report, take a stored file,
 * start or stop a schedule, subscribe, share, copy, delete, restore.
 *
 * The run paths are the ones the page always had, carried over unchanged in
 * what they ask the engine for:
 *
 *   - a saved pipeline report renders as the Pipeline toolbar renders it (its
 *     columns, its filters, the one mirror setting), and a view scoped to dead
 *     deals reads the dead book, not an empty live one;
 *   - any other saved report renders with the configuration it was saved with;
 *   - a schedule's Run now makes a retained preview and sends nothing;
 *   - a generated file runs again with the filters it was first made with.
 *
 * What changed is that each one says, truthfully, whether the file was kept.
 */
import { useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { loansForScope, useModuleDemoEnabled } from '@/hooks/useDemoDataAccess'
import { markDemoFilename } from '@/config/demoExportMark'
import { reportsService } from '@/services/loans'
import { reportingCenterService } from '@/services/reportingCenter'
import { deliveryModeFor, reportSchedulesService, scheduleInput } from '@/services/reportSchedules'
import { reportDefinitionsService, type ReportDefinition } from '@/services/reportDefinitions'
import { pipelineExportSettingsService } from '@/services/pipelineExportSettings'
import type { Loan } from '@/types'
import type { ReportSchedule } from '../../pipeline-intelligence/types'
import { templateNeedsLoan } from '../../pipeline-intelligence/types'
import { exportSavedViewXlsx, savedViewExportPlan, viewFilters } from '../../pipeline-intelligence/savedViewExport'
import { dealScopeOf } from '../../pipeline-intelligence/helpers'
import { reportTypeSpec } from '../reportTypes'
import type { LibraryReport, LibraryRun } from './library'

export type RunFormat = 'pdf' | 'xlsx'

export type Notice = { tone: 'ok' | 'bad'; text: string }

export function downloadArtifact(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  anchor.rel = 'noopener'
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000)
}

/** A readable message for a failed request, including one whose body is a Blob (a failed download). */
export async function requestErrorMessage(cause: unknown, fallback: string): Promise<string> {
  const response = (cause as { response?: { data?: unknown; status?: number } })?.response
  const data = response?.data
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { detail?: string }
      if (parsed?.detail) return parsed.detail
    } catch {
      /* fall through */
    }
  } else if (data && typeof data === 'object' && 'detail' in data) {
    const detail = (data as { detail?: unknown }).detail
    if (typeof detail === 'string') return detail
  }
  if (response?.status === 403) return 'Your role does not allow that.'
  if (cause instanceof Error && cause.message && !/^Request failed with status code/.test(cause.message)) {
    return cause.message
  }
  return fallback
}

const fileBase = (title: string) => title.replace(/\s+/g, '-').toLowerCase()

type RunRequest = { report: LibraryReport; format?: RunFormat }

export function useReportActions({
  deals,
  me,
  setNotice,
}: {
  deals: Loan[]
  me: string | null
  setNotice: (notice: Notice | null) => void
}) {
  const queryClient = useQueryClient()
  const reportsDemo = useModuleDemoEnabled('reports')

  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['report-definitions'] }),
      // A pipeline report is both a definition (this library) and a saved view (the Studio's
      // list): changing one without the other leaves the Studio unable to open what was made.
      queryClient.invalidateQueries({ queryKey: ['pipeline-views'] }),
      queryClient.invalidateQueries({ queryKey: ['report-history'] }),
      queryClient.invalidateQueries({ queryKey: ['report-schedules'] }),
      queryClient.invalidateQueries({ queryKey: ['report-delivery-history'] }),
    ])
  }, [queryClient])

  async function runDefinition(definition: ReportDefinition, title: string, wanted: RunFormat | undefined): Promise<string> {
    const spec = reportTypeSpec(definition.reportType)
    const format: RunFormat = wanted && spec.formats.includes(wanted) ? wanted : spec.formats[0]
    if (definition.reportType === 'pipeline') {
      // A saved pipeline view renders exactly what the Pipeline toolbar renders for it — its
      // columns, its filters, the one mirror setting — not the fixed layout over bare filters (CRE-06).
      const settings = await pipelineExportSettingsService.get()
      const filenameBase = fileBase(title)
      // A view scoped to dead deals would export EMPTY from the live book this page holds; the
      // scope names the deals it reads, so read them.
      const scopedDeals = await loansForScope(queryClient, dealScopeOf(viewFilters(definition)), deals, {
        demo: reportsDemo,
      })
      // Demo rows leave marked: the file must not read as the desk's own pipeline.
      const demo = { demo: reportsDemo }
      const plan = savedViewExportPlan(definition, scopedDeals, format, settings.useOnScreenColumns, demo)
      if (plan.kind === 'workbook') {
        await exportSavedViewXlsx(definition, plan.rows, filenameBase, undefined, undefined, demo)
        return 'Workbook built from the screen and downloaded. It is not kept under Runs.'
      }
      // The daily change bar has no Excel form (the engine refuses it); an Excel run takes the pipeline
      // summary, the pipeline template that does.
      const template = format === 'xlsx' && settings.template === 'pipeline_daily' ? 'pipeline_summary' : settings.template
      const response = await reportsService.generate({
        template,
        filters: plan.filters,
        format,
        definitionId: definition.id,
      })
      downloadArtifact(response.data as Blob, `${markDemoFilename(filenameBase, reportsDemo)}.${format}`)
      return 'Report generated and downloaded. A copy is kept under Runs.'
    }
    const filters = definition.filters
    if (templateNeedsLoan(spec.engineTemplate) && !filters.loan_id) {
      throw new Error('Choose a deal before running this report.')
    }
    const response = await reportsService.generate({
      template: spec.engineTemplate,
      filters,
      format,
      definitionId: definition.id,
    })
    downloadArtifact(response.data as Blob, `${fileBase(title)}.${format}`)
    return 'Report generated and downloaded. A copy is kept under Runs.'
  }

  const run = useMutation({
    mutationFn: async ({ report, format }: RunRequest): Promise<string> => {
      if (report.definition) return runDefinition(report.definition, report.name, format)
      const schedule = report.schedules[0]
      if (schedule) {
        // Run now on a schedule makes a retained preview. It never emails and never moves the cadence.
        await reportSchedulesService.runNow(schedule.id)
        return 'A preview was generated and kept under Runs. No email was sent and the schedule is unchanged.'
      }
      const latest = report.runs.find((entry) => entry.ok)
      if (!latest) throw new Error('There is no earlier run to repeat. Open the report to set it up.')
      // Re-run with the originating definition's filters, which the server stored with the
      // report — not `{}`, which reran it over the whole book.
      const response = await reportingCenterService.rerunHistory(latest.id)
      downloadArtifact(response.data as Blob, `${fileBase(report.name)}.${latest.format}`)
      return 'Report generated and downloaded. A copy is kept under Runs.'
    },
    onSuccess: async (message) => {
      setNotice({ tone: 'ok', text: message })
      await invalidate()
    },
    onError: async (cause) => setNotice({ tone: 'bad', text: await requestErrorMessage(cause, 'The report could not be generated.') }),
  })

  /** A schedule's own Run now, when the report also has a saved definition that Run now would use. */
  const runSchedule = useMutation({
    mutationFn: (schedule: ReportSchedule) => reportSchedulesService.runNow(schedule.id),
    onSuccess: async () => {
      setNotice({
        tone: 'ok',
        text: 'A preview was generated and kept under Runs. No email was sent and the schedule is unchanged.',
      })
      await invalidate()
    },
    onError: async (cause) => setNotice({ tone: 'bad', text: await requestErrorMessage(cause, 'The schedule could not be run.') }),
  })

  // The file a generated report produced on the day, as the archive holds it with its checksum —
  // not a re-run, which answers today's question.
  const downloadStored = useMutation({
    mutationFn: async ({ run: stored, name }: { run: Pick<LibraryRun, 'id' | 'format'>; name: string }) => {
      const { blob, filename } = await reportingCenterService.downloadHistory(
        stored.id,
        `${fileBase(name)}.${stored.format}`,
      )
      downloadArtifact(blob, filename)
    },
    onSuccess: () => setNotice({ tone: 'ok', text: 'Stored file downloaded.' }),
    onError: async (cause) =>
      setNotice({ tone: 'bad', text: await requestErrorMessage(cause, 'The stored file could not be downloaded.') }),
  })

  const toggleSchedule = useMutation({
    mutationFn: async (schedule: ReportSchedule) =>
      schedule.isActive ? reportSchedulesService.pause(schedule.id, schedule) : reportSchedulesService.activate(schedule.id),
    onSuccess: async (updated) => {
      setNotice({
        tone: 'ok',
        text: updated.isActive
          ? `${updated.name} is active.`
          : `${updated.name} is paused. It will not run until it is activated again.`,
      })
      await invalidate()
    },
    onError: async (cause) =>
      setNotice({ tone: 'bad', text: await requestErrorMessage(cause, 'The schedule could not be updated.') }),
  })

  const duplicate = useMutation({
    mutationFn: (id: string) => reportDefinitionsService.duplicate(id),
    onSuccess: async (copy) => {
      setNotice({ tone: 'ok', text: `Copied to “${copy.name}”, private to you.` })
      await invalidate()
    },
    onError: async (cause) =>
      setNotice({ tone: 'bad', text: await requestErrorMessage(cause, 'The report could not be copied.') }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => reportDefinitionsService.remove(id),
    onSuccess: async () => {
      setNotice({ tone: 'ok', text: 'Report deleted. Files it already made are still in the archive.' })
      await invalidate()
    },
    onError: async (cause) =>
      setNotice({ tone: 'bad', text: await requestErrorMessage(cause, 'The report could not be deleted.') }),
  })

  /**
   * Joining or leaving a recurring delivery.
   *
   * A subscription IS the recipient list — there is no second concept to keep in step with it, so
   * subscribing adds this reader's address to the schedule and unsubscribing takes it off. The
   * schedule is re-sent whole because the endpoint replaces the definition.
   *
   * The server pauses any schedule it is asked to change, so a change to who receives an ACTIVE
   * schedule needs it activated again; the message says so rather than claiming a delivery that
   * will not happen.
   */
  const subscribe = useMutation({
    mutationFn: async (schedule: ReportSchedule) => {
      if (!me) throw new Error('Sign in with an email address to subscribe.')
      const mine = me.toLowerCase()
      const already = schedule.recipients.some((to) => to.toLowerCase() === mine)
      const recipients = already
        ? schedule.recipients.filter((to) => to.toLowerCase() !== mine)
        : [...schedule.recipients, me]
      // The delivery mode follows the list, or the server will refuse to activate what this saves.
      const updated = await reportSchedulesService.update(
        schedule.id,
        scheduleInput(schedule, {
          recipients,
          deliveryMode: deliveryModeFor(recipients),
          senderEmail: recipients.length ? schedule.senderEmail : null,
        }),
      )
      return { updated, wasActive: schedule.isActive }
    },
    onSuccess: async ({ updated, wasActive }) => {
      const joined = updated.recipients.some((to) => to.toLowerCase() === (me ?? '').toLowerCase())
      const pausedNote = wasActive ? ' The schedule was paused by the change and needs to be activated again.' : ''
      setNotice({
        tone: 'ok',
        text: joined
          ? `Subscribed. ${updated.name} will be delivered to ${me} once it is active and delivery is on.${pausedNote}`
          : `Unsubscribed from ${updated.name}. It still runs for everyone else on it.${pausedNote}`,
      })
      await invalidate()
    },
    onError: async (cause) =>
      setNotice({ tone: 'bad', text: await requestErrorMessage(cause, 'The subscription could not be changed.') }),
  })

  const share = useMutation({
    mutationFn: ({ id, isShared }: { id: string; isShared: boolean }) =>
      reportDefinitionsService.update(id, {
        isShared,
        note: isShared ? 'Shared with the workspace' : 'Made private',
      }),
    onSuccess: async (updated) => {
      setNotice({
        tone: 'ok',
        text: updated.isShared
          ? `${updated.name} is shared with everyone who can see reports.`
          : `${updated.name} is private to its owner again.`,
      })
      await invalidate()
    },
    onError: async (cause) =>
      setNotice({ tone: 'bad', text: await requestErrorMessage(cause, 'Sharing could not be changed.') }),
  })

  const restore = useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) => reportDefinitionsService.restore(id, version),
    onSuccess: async (updated, { version }) => {
      setNotice({ tone: 'ok', text: `Restored version ${version} as version ${updated.version}. Nothing was lost from the history.` })
      await invalidate()
    },
    onError: async (cause) =>
      setNotice({ tone: 'bad', text: await requestErrorMessage(cause, 'That version could not be restored.') }),
  })

  return { run, runSchedule, downloadStored, toggleSchedule, duplicate, remove, subscribe, share, restore, invalidate }
}

export type ReportActions = ReturnType<typeof useReportActions>
